import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Heart, Send, CheckCircle, Building2, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicReferralPage() {
  const [form, setForm] = useState({ name: '', age_range: '', referral_source: '', race: '', description: '', referring_to: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.name) return toast.error('Name is required');
    if (!form.referring_to) return toast.error('Please select where to refer this person');
    setLoading(true);
    try {
      await axios.post(`${API}/api/public/referral`, form);
      setSubmitted(true);
    } catch { toast.error('Failed to submit'); } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-sky-50 flex items-center justify-center p-4" data-testid="referral-success">
        <Card className="max-w-md w-full rounded-2xl border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>Referral Submitted</h2>
            <p className="text-muted-foreground">A peer specialist will contact this person within <strong>48 hours</strong>.</p>
            <Button onClick={() => { setSubmitted(false); setForm({ name: '', age_range: '', referral_source: '', race: '', description: '', referring_to: '' }); }} variant="outline" className="mt-6 rounded-full">Submit Another Referral</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-sky-50 flex items-center justify-center p-4" data-testid="public-referral-page">
      <Card className="max-w-md w-full rounded-2xl border-0 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center mx-auto mb-3"><Heart className="w-6 h-6 text-white" /></div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Refer Someone for Support</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Quick and confidential. Takes less than 30 seconds.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5"><Label className="text-sm">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full name" data-testid="ref-name" /></div>
          
          {/* Referring To - Program Selection */}
          <div className="space-y-2">
            <Label className="text-sm">Refer To *</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setForm({...form, referring_to: 'purpose_driven'})}
                className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'purpose_driven' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'}`}
                data-testid="ref-to-purpose-driven"
              >
                <Building2 size={20} className={`mx-auto mb-1 ${form.referring_to === 'purpose_driven' ? 'text-teal-600' : 'text-slate-400'}`} />
                <p className={`text-xs font-medium ${form.referring_to === 'purpose_driven' ? 'text-teal-700' : 'text-slate-600'}`}>Purpose Driven</p>
                <p className="text-[10px] text-muted-foreground">Peer Support</p>
              </button>
              <button
                type="button"
                onClick={() => setForm({...form, referring_to: 'our_square'})}
                className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'our_square' ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:border-cyan-300'}`}
                data-testid="ref-to-our-square"
              >
                <Home size={20} className={`mx-auto mb-1 ${form.referring_to === 'our_square' ? 'text-cyan-600' : 'text-slate-400'}`} />
                <p className={`text-xs font-medium ${form.referring_to === 'our_square' ? 'text-cyan-700' : 'text-slate-600'}`}>Our Square</p>
                <p className="text-[10px] text-muted-foreground">Women's Housing</p>
              </button>
              <button
                type="button"
                onClick={() => setForm({...form, referring_to: 'both'})}
                className={`p-3 rounded-xl border-2 transition-all text-center ${form.referring_to === 'both' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-300'}`}
                data-testid="ref-to-both"
              >
                <div className="flex justify-center gap-0.5 mb-1">
                  <Building2 size={14} className={form.referring_to === 'both' ? 'text-purple-600' : 'text-slate-400'} />
                  <Home size={14} className={form.referring_to === 'both' ? 'text-purple-600' : 'text-slate-400'} />
                </div>
                <p className={`text-xs font-medium ${form.referring_to === 'both' ? 'text-purple-700' : 'text-slate-600'}`}>Both</p>
                <p className="text-[10px] text-muted-foreground">All Services</p>
              </button>
            </div>
          </div>
          
          <div className="space-y-1.5"><Label className="text-sm">Age Range</Label>
            <Select value={form.age_range} onValueChange={v => setForm({...form, age_range: v})}>
              <SelectTrigger data-testid="ref-age"><SelectValue placeholder="Select age range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="18-21">18-21</SelectItem>
                <SelectItem value="22-27">22-27</SelectItem>
                <SelectItem value="28-34">28-34</SelectItem>
                <SelectItem value="35-45">35-45</SelectItem>
                <SelectItem value="46-60">46-60</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-sm">Referral Source</Label>
            <Select value={form.referral_source} onValueChange={v => setForm({...form, referral_source: v})}>
              <SelectTrigger data-testid="ref-source"><SelectValue placeholder="Where is this referral from?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="probation">Probation</SelectItem>
                <SelectItem value="parole">Parole</SelectItem>
                <SelectItem value="treatment_center">Treatment Center</SelectItem>
                <SelectItem value="therapist">Therapist</SelectItem>
                <SelectItem value="case_manager">Case Manager</SelectItem>
                <SelectItem value="judge">Judge</SelectItem>
                <SelectItem value="self">Self-Referral</SelectItem>
                <SelectItem value="family">Family / Friend</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-sm">Race / Ethnicity (optional)</Label><Input value={form.race} onChange={e => setForm({...form, race: e.target.value})} placeholder="Optional" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Is there anything we should know? (optional)</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} placeholder="Brief description..." rows={2} data-testid="ref-description" showCleanup={false} /></div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="submit-referral">
            {loading ? 'Submitting...' : <><Send size={14} /> Submit Referral</>}
          </Button>
          <p className="text-xs text-center text-muted-foreground">All information is confidential.</p>
        </CardContent>
      </Card>
    </div>
  );
}
